import { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { API_SERVER } from '../config/env';
import { getAuthToken } from '../services/authSession';

const SOCKET_URL = typeof API_SERVER === 'string' ? API_SERVER.trim().replace(/\/+$/, '') : '';

export function useBeneficiaryMigrationSocket(clubId, options = {}) {
    const { onUpdate, enabled = true } = options;
    const [connected, setConnected] = useState(false);
    const onUpdateRef = useRef(onUpdate);
    const debounceRef = useRef(null);

    useEffect(() => {
        onUpdateRef.current = onUpdate;
    }, [onUpdate]);

    const room = clubId ? `migration:beneficiaries:${clubId}` : null;

    const flushUpdate = useCallback(payload => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            onUpdateRef.current?.(payload);
        }, 350);
    }, []);

    useEffect(() => {
        if (!enabled || !SOCKET_URL || !room) {
            setConnected(false);
            return undefined;
        }

        const token = getAuthToken();
        if (!token) {
            setConnected(false);
            return undefined;
        }

        const socket = io(SOCKET_URL, {
            auth: { token },
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: 12,
            reconnectionDelay: 1500,
        });

        const onConnect = () => {
            setConnected(true);
            socket.emit('join:room', room);
        };
        const onDisconnect = () => setConnected(false);
        const onQueueUpdate = payload => {
            if (payload?.clubId && Number(payload.clubId) !== Number(clubId)) return;
            flushUpdate(payload);
        };

        socket.on('connect', onConnect);
        socket.on('disconnect', onDisconnect);
        socket.on('migration:beneficiaries:update', onQueueUpdate);
        if (socket.connected) onConnect();

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
            socket.emit('leave:room', room);
            socket.off('connect', onConnect);
            socket.off('disconnect', onDisconnect);
            socket.off('migration:beneficiaries:update', onQueueUpdate);
            socket.disconnect();
            setConnected(false);
        };
    }, [clubId, enabled, room, flushUpdate]);

    return { connected, room };
}

export default useBeneficiaryMigrationSocket;
